import React, { useState, useCallback, useMemo } from "react";
import { css } from "@emotion/react";
import {
  Button,
  Heading,
} from "@phoenix/components";

// ==========================================
// SVGs & Icons
// ==========================================

export const OllamaServerIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0f766e"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
    <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
    <line x1="6" y1="6" x2="6.01" y2="6" />
    <line x1="6" y1="18" x2="6.01" y2="18" />
  </svg>
);

export const LlamaCppServerIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#2563eb"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
    <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
    <line x1="6" y1="6" x2="6.01" y2="6" />
    <line x1="6" y1="18" x2="6.01" y2="18" />
  </svg>
);

export const LocalAIServerIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0284c7"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);

export const VLLMServerIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#7c3aed"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m13 2-2 2.5V8l2-2.5V2z" />
    <path d="m7 7-4 5 4 5V7z" />
    <path d="m17 7 4 5-4 5V7z" />
  </svg>
);

const FormatAlignIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="15" y2="12" />
    <line x1="3" y1="18" x2="18" y2="18" />
  </svg>
);

const CodeBracketsIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

const ChevronDownIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const AlertTriangleIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ef4444"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

// ==========================================
// Types & Defaults
// ==========================================

export type LocalModelProviderType =
  | "ollama"
  | "llamacpp"
  | "llama-cpp"
  | "llamafile"
  | "localai"
  | "text-generation-webui"
  | "vllm";

export interface LocalModelTargetConfigValues {
  // Model Settings
  modelName: string;
  providerMode?: "chat" | "completion" | "embeddings";

  // Connection Settings
  serverUrl?: string;
  apiKey?: string;
  apiKeyEnv?: string;
  timeout?: number | string;

  // Generation Parameters
  temperature?: number | string;
  topP?: number | string;
  topK?: number | string;
  numPredict?: number | string; // Ollama predicted tokens
  numCtx?: number | string; // Ollama context size
  nPredict?: number | string; // llama.cpp prediction limit
  maxTokens?: number | string; // OpenAI-compatible max tokens
  repeatPenalty?: number | string;
  keepAlive?: string; // Ollama keep-alive duration (e.g. "5m")
  stopSequences?: string[] | string;
  thinkingMode?: boolean; // Ollama & vLLM reasoning options
  generationMode?: string; // Text Generation WebUI mode (e.g. "instruct", "chat")
  instructionTemplate?: string; // Text Generation WebUI instruction template

  // Advanced Options
  configJson: string;
  testGenerationInstructions: string;
  delayMs: number | string;
  extensionHook: string;
  prompts: string[];
}

export const DEFAULT_OLLAMA_TARGET_CONFIG: LocalModelTargetConfigValues = {
  modelName: "ollama:llama3",
  providerMode: "chat",
  serverUrl: "http://localhost:11434",
  apiKey: "",
  apiKeyEnv: "OLLAMA_API_KEY",
  timeout: 60,
  temperature: 0.7,
  topP: 0.9,
  topK: 40,
  numPredict: 512,
  numCtx: 2048,
  repeatPenalty: 1.1,
  keepAlive: "5m",
  thinkingMode: false,
  configJson: "{}",
  testGenerationInstructions: "",
  delayMs: 0,
  extensionHook: "",
  prompts: ["{{prompt}}"],
};

export const DEFAULT_LLAMACPP_TARGET_CONFIG: LocalModelTargetConfigValues = {
  modelName: "llama.cpp:http://localhost:8080/completion",
  providerMode: "completion",
  serverUrl: "http://localhost:8080/completion",
  timeout: 60,
  temperature: 0.7,
  topP: 0.95,
  topK: 40,
  nPredict: 512,
  repeatPenalty: 1.1,
  stopSequences: "",
  configJson: "{}",
  testGenerationInstructions: "",
  delayMs: 0,
  extensionHook: "",
  prompts: ["{{prompt}}"],
};

export const DEFAULT_LLAMAFILE_TARGET_CONFIG: LocalModelTargetConfigValues = {
  modelName: "LLaMA_CPP",
  providerMode: "chat",
  serverUrl: "http://localhost:8080/v1",
  apiKey: "",
  apiKeyEnv: "LLAMAFILE_API_KEY",
  timeout: 60,
  temperature: 0.7,
  maxTokens: 1024,
  configJson: "{}",
  testGenerationInstructions: "",
  delayMs: 0,
  extensionHook: "",
  prompts: ["{{prompt}}"],
};

export const DEFAULT_LOCALAI_TARGET_CONFIG: LocalModelTargetConfigValues = {
  modelName: "localai:chat:gpt-4",
  providerMode: "chat",
  serverUrl: "http://localhost:8080/v1",
  apiKey: "",
  apiKeyEnv: "LOCALAI_API_KEY",
  timeout: 60,
  temperature: 0.7,
  maxTokens: 1024,
  configJson: "{}",
  testGenerationInstructions: "",
  delayMs: 0,
  extensionHook: "",
  prompts: ["{{prompt}}"],
};

export const DEFAULT_TEXTGEN_WEBUI_TARGET_CONFIG: LocalModelTargetConfigValues = {
  modelName: "default",
  providerMode: "chat",
  serverUrl: "http://localhost:5000/v1",
  apiKey: "",
  apiKeyEnv: "TEXTGEN_API_KEY",
  timeout: 60,
  temperature: 0.7,
  maxTokens: 1024,
  generationMode: "instruct",
  instructionTemplate: "",
  configJson: "{}",
  testGenerationInstructions: "",
  delayMs: 0,
  extensionHook: "",
  prompts: ["{{prompt}}"],
};

export const DEFAULT_VLLM_TARGET_CONFIG: LocalModelTargetConfigValues = {
  modelName: "meta-llama/Llama-2-7b-chat-hf",
  providerMode: "chat",
  serverUrl: "http://localhost:8000/v1",
  apiKey: "",
  apiKeyEnv: "VLLM_API_KEY",
  timeout: 60,
  temperature: 0.7,
  maxTokens: 1024,
  thinkingMode: false,
  configJson: "{}",
  testGenerationInstructions: "",
  delayMs: 0,
  extensionHook: "",
  prompts: ["{{prompt}}"],
};

export const OLLAMA_EXAMPLE_CONFIG = {
  host: "http://localhost:11434",
  temperature: 0.7,
  num_predict: 512,
  num_ctx: 2048,
  top_k: 40,
  top_p: 0.9,
  repeat_penalty: 1.1,
  keep_alive: "5m",
};

export const LLAMACPP_EXAMPLE_CONFIG = {
  temperature: 0.7,
  n_predict: 512,
  top_k: 40,
  top_p: 0.95,
  repeat_penalty: 1.1,
  stop: ["</s>", "User:"],
};

export const LLAMAFILE_EXAMPLE_CONFIG = {
  apiBaseUrl: "http://localhost:8080/v1",
  temperature: 0.7,
  max_tokens: 1024,
};

export const LOCALAI_EXAMPLE_CONFIG = {
  apiBaseUrl: "http://localhost:8080/v1",
  temperature: 0.7,
  max_tokens: 1024,
};

export const TEXTGEN_WEBUI_EXAMPLE_CONFIG = {
  apiBaseUrl: "http://localhost:5000/v1",
  temperature: 0.7,
  max_tokens: 1024,
  mode: "instruct",
};

export const VLLM_EXAMPLE_CONFIG = {
  apiBaseUrl: "http://localhost:8000/v1",
  temperature: 0.7,
  max_tokens: 1024,
};

// ==========================================
// Provider Metadata
// ==========================================

export interface LocalModelProviderMeta {
  type: LocalModelProviderType;
  title: string;
  fieldLabel: string;
  fieldId: string;
  fieldPlaceholder: string;
  fieldHelpPrefix: string;
  defaultServerUrl: string;
  docUrl?: string;
  configDescription: string;
  examplesTitle: string;
  exampleItems: Array<{ code: string; desc: string }>;
  exampleConfig: Record<string, any>;
  icon: React.ComponentType;
  validationRequiredMessage: string;
  requiresServerUrl?: boolean;
  supportsModes?: boolean;
  notes?: string;
}

export const LOCAL_MODEL_PROVIDERS: Record<string, LocalModelProviderMeta> = {
  ollama: {
    type: "ollama",
    title: "Ollama",
    fieldLabel: "Model Name",
    fieldId: "ollama-model-input",
    fieldPlaceholder: "llama3.2",
    fieldHelpPrefix: "Ollama model name. Make sure Ollama is running locally.",
    defaultServerUrl: "http://localhost:11434",
    configDescription: "Ollama server URL and model parameters",
    examplesTitle: "Ollama Configuration Examples",
    exampleItems: [
      { code: "ollama:chat:llama3.2", desc: "- Explicit chat mode" },
      { code: "ollama:completion:llama3.2", desc: "- Text completion mode" },
      { code: "ollama:embeddings:nomic-embed-text", desc: "- Embeddings model" },
    ],
    exampleConfig: OLLAMA_EXAMPLE_CONFIG,
    icon: OllamaServerIcon,
    validationRequiredMessage: "Ollama model name is required",
    requiresServerUrl: true,
    supportsModes: true,
    notes: "Ollama uses num_predict for token control and supports keep_alive and reasoning settings.",
  },
  llamacpp: {
    type: "llamacpp",
    title: "llama.cpp",
    fieldLabel: "Server URL",
    fieldId: "llamacpp-server-url-input",
    fieldPlaceholder: "llama.cpp:http://localhost:8080/completion",
    fieldHelpPrefix: "llama.cpp server URL. Start the server first.",
    defaultServerUrl: "http://localhost:8080/completion",
    configDescription: "Generation parameters",
    examplesTitle: "llama.cpp Server Examples",
    exampleItems: [
      { code: "llama.cpp:http://localhost:8080/completion", desc: "- Default completion endpoint" },
      { code: "llama:7b", desc: "- Model identifier syntax" },
    ],
    exampleConfig: LLAMACPP_EXAMPLE_CONFIG,
    icon: LlamaCppServerIcon,
    validationRequiredMessage: "llama.cpp server URL is required",
    requiresServerUrl: true,
    notes: "llama.cpp server uses n_predict for predicted token limit and supports stop sequences.",
  },
  "llama-cpp": {
    type: "llama-cpp",
    title: "llama.cpp",
    fieldLabel: "Server URL",
    fieldId: "llamacpp-server-url-input",
    fieldPlaceholder: "llama.cpp:http://localhost:8080/completion",
    fieldHelpPrefix: "llama.cpp server URL. Start the server first.",
    defaultServerUrl: "http://localhost:8080/completion",
    configDescription: "Generation parameters",
    examplesTitle: "llama.cpp Server Examples",
    exampleItems: [
      { code: "llama.cpp:http://localhost:8080/completion", desc: "- Default completion endpoint" },
      { code: "llama:7b", desc: "- Model identifier syntax" },
    ],
    exampleConfig: LLAMACPP_EXAMPLE_CONFIG,
    icon: LlamaCppServerIcon,
    validationRequiredMessage: "llama.cpp server URL is required",
    requiresServerUrl: true,
    notes: "llama.cpp server uses n_predict for predicted token limit and supports stop sequences.",
  },
  llamafile: {
    type: "llamafile",
    title: "Llamafile",
    fieldLabel: "Model Identifier",
    fieldId: "llamafile-model-input",
    fieldPlaceholder: "LLaMA_CPP",
    fieldHelpPrefix: "Llamafile model identifier running on an OpenAI-compatible endpoint.",
    defaultServerUrl: "http://localhost:8080/v1",
    configDescription: "OpenAI-compatible host and sampling parameters",
    examplesTitle: "Llamafile Configuration Examples",
    exampleItems: [
      { code: "LLaMA_CPP", desc: "- Standard llamafile identifier" },
      { code: "openai:chat:LLaMA_CPP", desc: "- Explicit chat format with apiBaseUrl" },
    ],
    exampleConfig: LLAMAFILE_EXAMPLE_CONFIG,
    icon: LlamaCppServerIcon,
    validationRequiredMessage: "Llamafile model identifier is required",
    requiresServerUrl: true,
    notes: "Llamafile exposes an OpenAI-compatible API. The server URL is configured separately from the model name.",
  },
  localai: {
    type: "localai",
    title: "LocalAI",
    fieldLabel: "Model Name",
    fieldId: "localai-model-input",
    fieldPlaceholder: "localai:chat:gpt-4",
    fieldHelpPrefix: "LocalAI model name. Make sure LocalAI is running on your system.",
    defaultServerUrl: "http://localhost:8080/v1",
    configDescription: "LocalAI server configuration and model parameters",
    examplesTitle: "LocalAI Configuration Examples",
    exampleItems: [
      { code: "localai:chat:gpt-4", desc: "- OpenAI-compatible chat format" },
      { code: "localai:completion:hermes-2-pro", desc: "- Completion format" },
      { code: "localai:embeddings:text-embedding-ada-002", desc: "- Embeddings model" },
    ],
    exampleConfig: LOCALAI_EXAMPLE_CONFIG,
    icon: LocalAIServerIcon,
    validationRequiredMessage: "LocalAI model name is required",
    requiresServerUrl: true,
    supportsModes: true,
    notes: "LocalAI provides drop-in OpenAI-compatible endpoints with support for optional authentication.",
  },
  "text-generation-webui": {
    type: "text-generation-webui",
    title: "Text Generation WebUI",
    fieldLabel: "Model Name / ID",
    fieldId: "textgen-webui-model-input",
    fieldPlaceholder: "default",
    fieldHelpPrefix: "Text Generation WebUI model name with OpenAI extension enabled.",
    defaultServerUrl: "http://localhost:5000/v1",
    configDescription: "Generation parameters and instruction mode",
    examplesTitle: "Text Generation WebUI Configuration Examples",
    exampleItems: [
      { code: "default", desc: "- Current loaded model in WebUI" },
      { code: "openai:chat:default", desc: "- Explicit OpenAI chat format with apiBaseUrl" },
    ],
    exampleConfig: TEXTGEN_WEBUI_EXAMPLE_CONFIG,
    icon: LocalAIServerIcon,
    validationRequiredMessage: "Model identifier is required",
    requiresServerUrl: true,
    notes: "The OpenAI extension (--api) must be enabled on the Text Generation WebUI server.",
  },
  vllm: {
    type: "vllm",
    title: "vLLM",
    fieldLabel: "Model Identifier / Served Alias",
    fieldId: "vllm-model-input",
    fieldPlaceholder: "meta-llama/Llama-2-7b-chat-hf",
    fieldHelpPrefix: "vLLM served model alias. Must match the model name exposed by the server.",
    defaultServerUrl: "http://localhost:8000/v1",
    configDescription: "OpenAI-compatible parameters and server settings",
    examplesTitle: "vLLM Configuration Examples",
    exampleItems: [
      { code: "meta-llama/Llama-2-7b-chat-hf", desc: "- Hugging Face served alias" },
      { code: "openai:chat:meta-llama/Llama-2-7b-chat-hf", desc: "- Explicit chat format with apiBaseUrl" },
    ],
    exampleConfig: VLLM_EXAMPLE_CONFIG,
    icon: VLLMServerIcon,
    validationRequiredMessage: "vLLM served model name is required",
    requiresServerUrl: true,
    supportsModes: true,
    notes: "The configured model must match the model name exposed by the vLLM server (served alias rather than raw disk path).",
  },
};

// ==========================================
// Formatting & Validation Helpers
// ==========================================

export function formatLocalModelConfigJson(jsonStr: string): { formatted?: string; error?: string } {
  if (!jsonStr || !jsonStr.trim()) {
    return { formatted: "{}" };
  }
  try {
    const parsed = JSON.parse(jsonStr);
    return { formatted: JSON.stringify(parsed, null, 2) };
  } catch (err: any) {
    return { error: "Invalid JSON: " + (err?.message || "Syntax error") };
  }
}

export function validateLocalModelTargetConfig(
  values: LocalModelTargetConfigValues,
  providerType: LocalModelProviderType = "ollama"
): {
  isValid: boolean;
  errors: { modelName?: string; serverUrl?: string; configJson?: string; temperature?: string };
} {
  const errors: { modelName?: string; serverUrl?: string; configJson?: string; temperature?: string } = {};
  const meta = LOCAL_MODEL_PROVIDERS[providerType] || LOCAL_MODEL_PROVIDERS.ollama;

  if (!values.modelName || !values.modelName.trim()) {
    errors.modelName = meta.validationRequiredMessage;
  }

  if (values.serverUrl && !values.serverUrl.trim()) {
    errors.serverUrl = "Server URL cannot be empty";
  }

  if (values.temperature !== undefined && values.temperature !== "") {
    const t = Number(values.temperature);
    if (isNaN(t) || t < 0 || t > 2) {
      errors.temperature = "Temperature must be between 0.0 and 2.0";
    }
  }

  if (values.configJson && values.configJson.trim()) {
    try {
      JSON.parse(values.configJson);
    } catch (err: any) {
      errors.configJson = "Invalid JSON configuration: " + (err?.message || "Syntax error");
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

// ==========================================
// Styles
// ==========================================

const cardStyle = css`
  background: var(--global-color-surface-lowest, #ffffff);
  border: 1px solid var(--global-border-color-default, #e2e8f0);
  border-radius: 12px;
  padding: 32px 36px 40px 36px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;

  .theme--dark & {
    background: #1e293b !important;
    border-color: #334155 !important;
  }
`;

const sectionStyle = css`
  margin-bottom: 28px;
  padding-bottom: 24px;
  border-bottom: 1px solid var(--global-border-color-default, #e2e8f0);

  .theme--dark & {
    border-color: #334155 !important;
  }
`;

const sectionHeadingStyle = css`
  font-size: 1.05rem;
  font-weight: 750;
  color: var(--global-text-color-900, #0f172a);
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  .theme--dark & {
    color: #ffffff !important;
  }
`;

const sectionSubtitleStyle = css`
  font-size: 0.84rem;
  color: var(--global-text-color-700, #64748b);
  margin: 0 0 16px 0;

  .theme--dark & {
    color: #94a3b8 !important;
  }
`;

const fieldLabelStyle = css`
  display: block;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--global-text-color-900, #0f172a);
  margin-bottom: 6px;
  font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;

  .theme--dark & {
    color: #ffffff !important;
  }
`;

const textInputStyle = (hasError: boolean = false) => css`
  width: 100%;
  box-sizing: border-box;
  height: 42px;
  padding: 0 14px;
  border: 1px solid ${hasError ? "#ef4444" : "var(--global-border-color-default, #e2e8f0)"};
  border-radius: 8px;
  font-size: 0.9rem;
  color: var(--global-text-color-900, #0f172a);
  background: var(--global-color-surface-lowest, #ffffff);
  outline: none;
  font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
  transition: all 0.15s ease;

  &:focus {
    border-color: ${hasError ? "#ef4444" : "#0f766e"};
    box-shadow: 0 0 0 3px ${hasError ? "rgba(239, 68, 68, 0.15)" : "rgba(15, 118, 110, 0.15)"};
  }

  .theme--dark & {
    background: #0f172a !important;
    border-color: ${hasError ? "#ef4444" : "#334155"} !important;
    color: #ffffff !important;
  }
`;

const selectStyle = css`
  width: 100%;
  box-sizing: border-box;
  height: 42px;
  padding: 0 14px;
  border: 1px solid var(--global-border-color-default, #e2e8f0);
  border-radius: 8px;
  font-size: 0.9rem;
  color: var(--global-text-color-900, #0f172a);
  background: var(--global-color-surface-lowest, #ffffff);
  outline: none;
  font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
  transition: all 0.15s ease;

  &:focus {
    border-color: #0f766e;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.15);
  }

  .theme--dark & {
    background: #0f172a !important;
    border-color: #334155 !important;
    color: #ffffff !important;
  }
`;

const helpTextStyle = css`
  margin-top: 5px;
  font-size: 0.81rem;
  color: var(--global-text-color-700, #64748b);
  line-height: 1.45;
  font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;

  a {
    color: #0f766e;
    text-decoration: underline;
    font-weight: 600;

    &:hover {
      color: #0d9488;
    }
  }

  .theme--dark & {
    color: #94a3b8 !important;

    a {
      color: #2dd4bf !important;
    }
  }
`;

const noticeBoxStyle = css`
  padding: 12px 16px;
  border-radius: 8px;
  background: #f0fdfa;
  border: 1px solid #99f6e4;
  color: #0f766e;
  font-size: 0.84rem;
  line-height: 1.5;
  margin-top: 10px;

  .theme--dark & {
    background: rgba(15, 118, 110, 0.15) !important;
    border-color: #115e59 !important;
    color: #2dd4bf !important;
  }
`;

const gridTwoCol = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const accordionCardStyle = css`
  border: 1px solid var(--global-border-color-default, #e2e8f0);
  border-radius: 8px;
  margin-top: 14px;
  background: var(--global-color-surface-lowest, #ffffff);
  overflow: hidden;
  transition: all 0.15s ease;

  .theme--dark & {
    background: #0f172a !important;
    border-color: #334155 !important;
  }
`;

const accordionHeaderStyle = css`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;

  &:hover {
    background: rgba(0, 0, 0, 0.015);
  }

  .theme--dark &:hover {
    background: rgba(255, 255, 255, 0.03) !important;
  }
`;

const accordionTitleStyle = css`
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--global-text-color-900, #0f172a);
  margin: 0;

  .theme--dark & {
    color: #ffffff !important;
  }
`;

const accordionSubtitleStyle = css`
  font-size: 0.8rem;
  color: var(--global-text-color-700, #64748b);
  margin: 2px 0 0 0;

  .theme--dark & {
    color: #94a3b8 !important;
  }
`;

const accordionBodyStyle = css`
  padding: 16px 18px;
  border-top: 1px solid var(--global-border-color-default, #e2e8f0);
  background: var(--global-color-surface-lowest, #ffffff);

  .theme--dark & {
    background: #0f172a !important;
    border-color: #334155 !important;
  }
`;

const jsonEditorWrapper = css`
  border: 1px solid var(--global-border-color-default, #e2e8f0);
  border-radius: 8px;
  background: var(--global-color-surface-lowest, #ffffff);
  overflow: hidden;
  margin-top: 8px;

  .theme--dark & {
    background: #0f172a !important;
    border-color: #334155 !important;
  }
`;

const jsonHeaderTab = css`
  padding: 8px 14px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #64748b;
  border-bottom: 1px solid var(--global-border-color-default, #e2e8f0);
  background: #f8fafc;
  letter-spacing: 0.03em;

  .theme--dark & {
    background: #1e293b !important;
    border-color: #334155 !important;
    color: #94a3b8 !important;
  }
`;

const jsonTextArea = css`
  width: 100%;
  box-sizing: border-box;
  min-height: 130px;
  padding: 12px 14px;
  border: none;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 0.86rem;
  color: var(--global-text-color-900, #0f172a);
  background: transparent;
  outline: none;
  resize: vertical;
  line-height: 1.5;

  .theme--dark & {
    color: #ffffff !important;
  }
`;

const promptCardStyle = css`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 10px;
`;

const promptTextAreaStyle = css`
  flex: 1;
  box-sizing: border-box;
  min-height: 56px;
  padding: 10px 14px;
  border: 1px solid var(--global-border-color-default, #e2e8f0);
  border-radius: 8px;
  font-size: 0.9rem;
  font-family: inherit;
  color: var(--global-text-color-900, #0f172a);
  background: var(--global-color-surface-lowest, #ffffff);
  outline: none;
  resize: vertical;

  &:focus {
    border-color: #0f766e;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.15);
  }

  .theme--dark & {
    background: #0f172a !important;
    border-color: #334155 !important;
    color: #ffffff !important;
  }
`;

const deleteButtonStyle = css`
  padding: 8px;
  background: transparent;
  border: none;
  color: #94a3b8;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    background: #fee2e2;
    color: #ef4444;
  }

  .theme--dark &:hover {
    background: rgba(239, 68, 68, 0.2) !important;
    color: #f87171 !important;
  }
`;

const addPromptBtnStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #f0fdfa;
  border: 1px dashed #0f766e;
  border-radius: 8px;
  color: #0f766e;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;

  &:hover {
    background: #ccfbf1;
  }

  .theme--dark & {
    background: rgba(15, 118, 110, 0.2) !important;
    color: #2dd4bf !important;
    border-color: #2dd4bf !important;
  }
`;

const formatBtnStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: #0f766e;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background 0.15s ease;
  font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;

  &:hover {
    background: rgba(15, 118, 110, 0.08);
  }

  .theme--dark & {
    color: #2dd4bf !important;
  }
`;

const examplesBtnStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 14px;
  background: #f8fafc;
  border: 1px solid var(--global-border-color-default, #e2e8f0);
  border-radius: 8px;
  color: #0f172a;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
  font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;

  &:hover {
    background: #f1f5f9;
  }

  .theme--dark & {
    background: #1e293b !important;
    border-color: #334155 !important;
    color: #ffffff !important;
  }
`;

const exampleDropdownStyle = css`
  margin-top: 8px;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid var(--global-border-color-default, #e2e8f0);
  border-radius: 8px;
  font-size: 0.82rem;

  .theme--dark & {
    background: #1e293b !important;
    border-color: #334155 !important;
  }
`;

const testConnectionCardStyle = (success?: boolean) => css`
  margin-top: 14px;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid ${success ? "#a7f3d0" : "#fca5a5"};
  background: ${success ? "#ecfdf5" : "#fef2f2"};
  color: ${success ? "#065f46" : "#991b1b"};
  font-size: 0.84rem;
  font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
`;

const bottomNavWrapper = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 36px;
  padding-top: 24px;
  border-top: 1px solid var(--global-border-color-default, #e2e8f0);
`;

const backBtnStyle = css`
  padding: 10px 22px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  color: #0f172a;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
  transition: all 0.15s ease;

  &:hover {
    background: #f8fafc;
    border-color: #94a3b8;
  }

  .theme--dark & {
    background: #1e293b !important;
    border-color: #334155 !important;
    color: #ffffff !important;
  }
`;

const nextBtnStyle = css`
  padding: 10px 26px;
  background: #0f766e;
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.88rem;
  font-weight: 750;
  cursor: pointer;
  font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
  transition: all 0.15s ease;

  &:hover {
    background: #0d9488;
  }

  .theme--dark & {
    background: #0f766e !important;
  }
`;

// ==========================================
// Component Props
// ==========================================

export interface LocalModelTargetConfigurationProps {
  providerType: LocalModelProviderType;
  targetName?: string;
  values: LocalModelTargetConfigValues;
  onChange: (updates: Partial<LocalModelTargetConfigValues>) => void;
  onBack?: () => void;
  onNext?: () => void;
  hideBottomBar?: boolean;
  renderModeSwitcher?: React.ReactNode;
  onTestConnection?: () => Promise<void> | void;
  isTestingConnection?: boolean;
  testResult?: { success: boolean; message: string; latency?: string } | null;
}

export function LocalModelTargetConfiguration({
  providerType,
  targetName = "support-agent",
  values,
  onChange,
  onBack,
  onNext,
  hideBottomBar = false,
  renderModeSwitcher,
  onTestConnection,
  isTestingConnection = false,
  testResult,
}: LocalModelTargetConfigurationProps) {
  const meta = LOCAL_MODEL_PROVIDERS[providerType] || LOCAL_MODEL_PROVIDERS.ollama;
  const ProviderIcon = meta.icon;

  // Accordion open states
  const [isExamplesOpen, setIsExamplesOpen] = useState(false);
  const [isTestGenOpen, setIsTestGenOpen] = useState(false);
  const [isDelayOpen, setIsDelayOpen] = useState(false);
  const [isExtensionHookOpen, setIsExtensionHookOpen] = useState(false);

  // Validation & JSON Error
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Compute live provider ID preview
  const liveProviderId = useMemo(() => {
    const raw = values.modelName?.trim() || "";
    const cleanModel = raw.replace(
      /^(ollama:(chat:|completion:)?|llama\.cpp:|localai:(chat:|completion:)?|openai:(chat:|completion:)?)/,
      ""
    );
    const mode = values.providerMode || "chat";
    if (providerType === "ollama") {
      return `ollama:${mode}:${cleanModel || "llama3.2"}`;
    } else if (providerType === "llamacpp" || providerType === "llama-cpp") {
      return `llama.cpp:${values.serverUrl || meta.defaultServerUrl}`;
    } else if (providerType === "localai") {
      return `localai:${mode}:${cleanModel || "gpt-4"}`;
    } else if (providerType === "vllm") {
      return `openai:${mode}:${cleanModel || "meta-llama/Llama-2-7b-chat-hf"}`;
    } else if (providerType === "llamafile") {
      return `openai:chat:${cleanModel || "LLaMA_CPP"}`;
    } else {
      return `openai:chat:${cleanModel || "default"}`;
    }
  }, [providerType, values.modelName, values.providerMode, values.serverUrl, meta.defaultServerUrl]);

  // Format JSON handler
  const handleFormatJson = useCallback(() => {
    try {
      const parsed = JSON.parse(values.configJson || "{}");
      const formatted = JSON.stringify(parsed, null, 2);
      onChange({ configJson: formatted });
      setJsonError(null);
    } catch {
      setJsonError("Invalid JSON syntax. Please verify before formatting.");
    }
  }, [values.configJson, onChange]);

  // Load example configuration
  const handleLoadExample = useCallback(() => {
    onChange({ configJson: JSON.stringify(meta.exampleConfig, null, 2) });
    setJsonError(null);
  }, [meta.exampleConfig, onChange]);

  // Prompt handlers
  const handlePromptChange = useCallback(
    (index: number, newText: string) => {
      const updated = [...(values.prompts || ["{{prompt}}"])];
      updated[index] = newText;
      onChange({ prompts: updated });
    },
    [values.prompts, onChange]
  );

  const handleAddPrompt = useCallback(() => {
    const updated = [...(values.prompts || ["{{prompt}}"]), "{{prompt}}"];
    onChange({ prompts: updated });
  }, [values.prompts, onChange]);

  const handleDeletePrompt = useCallback(
    (index: number) => {
      const updated = [...(values.prompts || ["{{prompt}}"])];
      if (updated.length <= 1) return;
      updated.splice(index, 1);
      onChange({ prompts: updated });
    },
    [values.prompts, onChange]
  );

  // Validate on Next
  const handleNextClick = () => {
    if (!values.modelName || !values.modelName.trim()) {
      setFieldError(meta.validationRequiredMessage);
      return;
    }
    setFieldError(null);

    if (values.configJson && values.configJson.trim() !== "") {
      try {
        JSON.parse(values.configJson);
      } catch {
        setJsonError("Invalid JSON configuration. Please fix syntax or clear.");
        return;
      }
    }

    if (onNext) onNext();
  };

  return (
    <div css={cardStyle}>
      {/* Header Row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", marginBottom: "24px" }}>
        <div>
          <Heading
            level={1}
            style={{
              fontSize: "1.35rem",
              fontWeight: 800,
              color: "#0f172a",
              margin: 0,
              letterSpacing: "-0.015em",
            }}
          >
            Configure Target: {targetName}
          </Heading>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "10px" }}>
            <span style={{ display: "inline-flex", alignItems: "center" }}><ProviderIcon /></span>
            <span style={{ fontSize: "1.05rem", fontWeight: 750, color: "#0f172a" }}>{meta.title}</span>
            <span
              style={{
                backgroundColor: "#f0fdfa",
                color: "#0f766e",
                border: "1px solid #99f6e4",
                borderRadius: "6px",
                padding: "2px 8px",
                fontSize: "0.74rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
              }}
            >
              Local Model
            </span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          {renderModeSwitcher && (
            <div style={{ flexShrink: 0 }}>
              {renderModeSwitcher}
            </div>
          )}
        </div>
      </div>

      {/* ===================================================================== */}
      {/* SECTION 1: CONNECTION SETTINGS */}
      {/* ===================================================================== */}
      <div css={sectionStyle}>
        <h3 css={sectionHeadingStyle}>1. Connection Settings</h3>
        <p css={sectionSubtitleStyle}>Endpoint location, server address, and connectivity verification</p>

        <div css={gridTwoCol}>
          <div>
            <label htmlFor={`${meta.fieldId}-server-url`} css={fieldLabelStyle}>
              Server URL <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              id={`${meta.fieldId}-server-url`}
              type="text"
              css={textInputStyle(false)}
              placeholder={meta.defaultServerUrl}
              value={values.serverUrl !== undefined ? values.serverUrl : meta.defaultServerUrl}
              onChange={(e) => onChange({ serverUrl: e.target.value })}
            />
            <div css={helpTextStyle}>
              Local server base endpoint. Default: <code>{meta.defaultServerUrl}</code>
            </div>
          </div>

          <div>
            <label htmlFor={`${meta.fieldId}-timeout`} css={fieldLabelStyle}>
              Request Timeout (Seconds)
            </label>
            <input
              id={`${meta.fieldId}-timeout`}
              type="number"
              min="1"
              max="120"
              css={textInputStyle(false)}
              placeholder="60"
              value={values.timeout ?? 60}
              onChange={(e) => onChange({ timeout: Number(e.target.value) || 60 })}
            />
            <div css={helpTextStyle}>Maximum allowed request duration before timing out (1-120s).</div>
          </div>
        </div>

        <div style={{ marginTop: "16px" }}>
          <div css={gridTwoCol}>
            <div>
              <label htmlFor={`${meta.fieldId}-api-key`} css={fieldLabelStyle}>
                API Key (Optional)
              </label>
              <input
                id={`${meta.fieldId}-api-key`}
                type="password"
                css={textInputStyle(false)}
                placeholder="sk-... or server secret"
                value={values.apiKey || ""}
                onChange={(e) => onChange({ apiKey: e.target.value })}
              />
              <div css={helpTextStyle}>Direct authentication key (if required by local proxy or tunnel).</div>
            </div>

            <div>
              <label htmlFor={`${meta.fieldId}-api-key-env`} css={fieldLabelStyle}>
                API Key Env Reference (Recommended)
              </label>
              <input
                id={`${meta.fieldId}-api-key-env`}
                type="text"
                css={textInputStyle(false)}
                placeholder="e.g. OLLAMA_API_KEY or ${credential_ref:LOCAL_KEY}"
                value={values.apiKeyEnv || ""}
                onChange={(e) => onChange({ apiKeyEnv: e.target.value })}
              />
              <div css={helpTextStyle}>Reference environment variable to keep credentials out of raw YAML.</div>
            </div>
          </div>
        </div>

        {/* Connection Testing Action */}
        <div style={{ marginTop: "20px", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          {onTestConnection && (
            <Button
              variant="secondary"
              onPress={onTestConnection}
              isDisabled={isTestingConnection}
              style={{
                padding: "8px 18px",
                fontSize: "0.86rem",
                fontWeight: 750,
              }}
            >
              {isTestingConnection ? "Testing Connection..." : "Test Target Connection"}
            </Button>
          )}
          <span style={{ fontSize: "0.82rem", color: "#64748b" }}>
            Validates reachability and verifies model presence against server tags/endpoints.
          </span>
        </div>

        {testResult && (
          <div css={testConnectionCardStyle(testResult.success)}>
            <strong>{testResult.success ? "Connection Successful" : "Connection Failed"}:</strong>{" "}
            {testResult.message}
            {testResult.latency && <span> (Latency: {testResult.latency})</span>}
          </div>
        )}
      </div>

      {/* ===================================================================== */}
      {/* SECTION 2: MODEL SETTINGS */}
      {/* ===================================================================== */}
      <div css={sectionStyle}>
        <h3 css={sectionHeadingStyle}>2. Model Settings</h3>
        <p css={sectionSubtitleStyle}>Model identifier, mode of operation, and provider configuration</p>

        <div css={gridTwoCol}>
          <div>
            <label htmlFor={meta.fieldId} css={fieldLabelStyle}>
              {meta.fieldLabel} <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              id={meta.fieldId}
              type="text"
              css={textInputStyle(!!fieldError)}
              placeholder={meta.fieldPlaceholder}
              value={values.modelName}
              onChange={(e) => {
                onChange({ modelName: e.target.value });
                if (fieldError) setFieldError(null);
              }}
            />
            <div css={helpTextStyle}>
              {meta.fieldHelpPrefix}
            </div>
            {fieldError && (
              <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#ef4444", fontSize: "0.82rem", marginTop: "6px" }}>
                <AlertTriangleIcon /> {fieldError}
              </div>
            )}
          </div>

          <div>
            <label htmlFor={`${meta.fieldId}-provider-mode`} css={fieldLabelStyle}>
              Provider Mode
            </label>
            <select
              id={`${meta.fieldId}-provider-mode`}
              css={selectStyle}
              value={values.providerMode || (providerType === "llamacpp" ? "completion" : "chat")}
              onChange={(e) => onChange({ providerMode: e.target.value as any })}
            >
              <option value="chat">Chat (Conversational endpoint /chat/completions or /api/chat)</option>
              <option value="completion">Completion (Raw text generation endpoint /completion or /api/generate)</option>
              <option value="embeddings">Embeddings (Vector representation)</option>
            </select>
            <div css={helpTextStyle}>Execution endpoint format for model inference.</div>
          </div>
        </div>

        {/* Live Provider ID preview pill */}
        <div style={{ marginTop: "14px", display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "#64748b" }}>Target Provider ID:</span>
          <code style={{ background: "#f1f5f9", padding: "4px 8px", borderRadius: "6px", fontSize: "0.84rem", fontWeight: 700, color: "#0f766e" }}>
            {liveProviderId}
          </code>
        </div>

        {meta.notes && (
          <div css={noticeBoxStyle}>
            💡 <strong>Provider Note:</strong> {meta.notes}
          </div>
        )}
      </div>

      {/* ===================================================================== */}
      {/* SECTION 3: GENERATION PARAMETERS */}
      {/* ===================================================================== */}
      <div css={sectionStyle}>
        <h3 css={sectionHeadingStyle}>3. Generation Parameters</h3>
        <p css={sectionSubtitleStyle}>Sampling temperature, token limits, penalties, and reasoning settings</p>

        <div css={gridTwoCol}>
          <div>
            <label htmlFor={`${meta.fieldId}-temperature`} css={fieldLabelStyle}>
              Temperature
            </label>
            <input
              id={`${meta.fieldId}-temperature`}
              type="number"
              step="0.05"
              min="0.0"
              max="2.0"
              css={textInputStyle(false)}
              placeholder="0.7"
              value={values.temperature ?? 0.7}
              onChange={(e) => onChange({ temperature: e.target.value === "" ? "" : Number(e.target.value) })}
            />
            <div css={helpTextStyle}>Sampling temperature (0.0 to 2.0). Higher values produce more creative output.</div>
          </div>

          {/* Provider-specific token limit inputs */}
          {providerType === "ollama" ? (
            <div>
              <label htmlFor={`${meta.fieldId}-num-predict`} css={fieldLabelStyle}>
                Predicted Tokens (<code>num_predict</code>)
              </label>
              <input
                id={`${meta.fieldId}-num-predict`}
                type="number"
                min="1"
                css={textInputStyle(false)}
                placeholder="512"
                value={values.numPredict ?? 512}
                onChange={(e) => onChange({ numPredict: Number(e.target.value) || "" })}
              />
              <div css={helpTextStyle}>Maximum number of tokens to predict. Ollama native API parameter.</div>
            </div>
          ) : providerType === "llamacpp" || providerType === "llama-cpp" ? (
            <div>
              <label htmlFor={`${meta.fieldId}-n-predict`} css={fieldLabelStyle}>
                Prediction Limit (<code>n_predict</code>)
              </label>
              <input
                id={`${meta.fieldId}-n-predict`}
                type="number"
                min="1"
                css={textInputStyle(false)}
                placeholder="512"
                value={values.nPredict ?? 512}
                onChange={(e) => onChange({ nPredict: Number(e.target.value) || "" })}
              />
              <div css={helpTextStyle}>Number of tokens to predict in llama.cpp server.</div>
            </div>
          ) : (
            <div>
              <label htmlFor={`${meta.fieldId}-max-tokens`} css={fieldLabelStyle}>
                Maximum Output Tokens (<code>max_tokens</code>)
              </label>
              <input
                id={`${meta.fieldId}-max-tokens`}
                type="number"
                min="1"
                css={textInputStyle(false)}
                placeholder="1024"
                value={values.maxTokens ?? 1024}
                onChange={(e) => onChange({ maxTokens: Number(e.target.value) || "" })}
              />
              <div css={helpTextStyle}>Max generation length for OpenAI-compatible local endpoints.</div>
            </div>
          )}
        </div>

        <div css={gridTwoCol} style={{ marginTop: "16px" }}>
          <div>
            <label htmlFor={`${meta.fieldId}-top-p`} css={fieldLabelStyle}>
              Top-p
            </label>
            <input
              id={`${meta.fieldId}-top-p`}
              type="number"
              step="0.05"
              min="0.0"
              max="1.0"
              css={textInputStyle(false)}
              placeholder="0.9"
              value={values.topP ?? 0.9}
              onChange={(e) => onChange({ topP: e.target.value === "" ? "" : Number(e.target.value) })}
            />
            <div css={helpTextStyle}>Nucleus sampling cutoff probability (0.0 to 1.0).</div>
          </div>

          <div>
            <label htmlFor={`${meta.fieldId}-top-k`} css={fieldLabelStyle}>
              Top-k
            </label>
            <input
              id={`${meta.fieldId}-top-k`}
              type="number"
              min="1"
              css={textInputStyle(false)}
              placeholder="40"
              value={values.topK ?? 40}
              onChange={(e) => onChange({ topK: e.target.value === "" ? "" : Number(e.target.value) })}
            />
            <div css={helpTextStyle}>Top-k token sampling filter.</div>
          </div>
        </div>

        {/* Additional sampling options */}
        <div css={gridTwoCol} style={{ marginTop: "16px" }}>
          {(providerType === "ollama" || providerType === "llamacpp" || providerType === "llama-cpp") && (
            <div>
              <label htmlFor={`${meta.fieldId}-repeat-penalty`} css={fieldLabelStyle}>
                Repeat Penalty
              </label>
              <input
                id={`${meta.fieldId}-repeat-penalty`}
                type="number"
                step="0.05"
                min="0.5"
                max="2.0"
                css={textInputStyle(false)}
                placeholder="1.1"
                value={values.repeatPenalty ?? 1.1}
                onChange={(e) => onChange({ repeatPenalty: e.target.value === "" ? "" : Number(e.target.value) })}
              />
              <div css={helpTextStyle}>Penalizes token repetitions in generated output.</div>
            </div>
          )}

          {providerType === "ollama" && (
            <div>
              <label htmlFor={`${meta.fieldId}-num-ctx`} css={fieldLabelStyle}>
                Context Size (<code>num_ctx</code>)
              </label>
              <input
                id={`${meta.fieldId}-num-ctx`}
                type="number"
                min="512"
                step="256"
                css={textInputStyle(false)}
                placeholder="2048"
                value={values.numCtx ?? 2048}
                onChange={(e) => onChange({ numCtx: Number(e.target.value) || "" })}
              />
              <div css={helpTextStyle}>Total context window size in tokens allocated by Ollama.</div>
            </div>
          )}

          {providerType === "ollama" && (
            <div>
              <label htmlFor={`${meta.fieldId}-keep-alive`} css={fieldLabelStyle}>
                Keep-Alive Duration
              </label>
              <input
                id={`${meta.fieldId}-keep-alive`}
                type="text"
                css={textInputStyle(false)}
                placeholder="5m"
                value={values.keepAlive ?? "5m"}
                onChange={(e) => onChange({ keepAlive: e.target.value })}
              />
              <div css={helpTextStyle}>Duration to keep model in memory after request (e.g. 5m, 1h, -1).</div>
            </div>
          )}

          {(providerType === "llamacpp" || providerType === "llama-cpp") && (
            <div>
              <label htmlFor={`${meta.fieldId}-stop`} css={fieldLabelStyle}>
                Stop Sequences
              </label>
              <input
                id={`${meta.fieldId}-stop`}
                type="text"
                css={textInputStyle(false)}
                placeholder="e.g. </s>, User:"
                value={typeof values.stopSequences === "string" ? values.stopSequences : (values.stopSequences || []).join(", ")}
                onChange={(e) => onChange({ stopSequences: e.target.value })}
              />
              <div css={helpTextStyle}>Stop sequences to halt text generation (comma-separated).</div>
            </div>
          )}

          {providerType === "text-generation-webui" && (
            <div>
              <label htmlFor={`${meta.fieldId}-gen-mode`} css={fieldLabelStyle}>
                Generation Mode
              </label>
              <input
                id={`${meta.fieldId}-gen-mode`}
                type="text"
                css={textInputStyle(false)}
                placeholder="instruct"
                value={values.generationMode || "instruct"}
                onChange={(e) => onChange({ generationMode: e.target.value })}
              />
              <div css={helpTextStyle}>Generation mode in WebUI (e.g. instruct, chat).</div>
            </div>
          )}
        </div>

        {/* Thinking / Reasoning toggle for reasoning models (e.g. DeepSeek-R1 on Ollama or vLLM) */}
        {(providerType === "ollama" || providerType === "vllm") && (
          <div style={{ marginTop: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
            <input
              id={`${meta.fieldId}-thinking`}
              type="checkbox"
              checked={Boolean(values.thinkingMode)}
              onChange={(e) => onChange({ thinkingMode: e.target.checked })}
              style={{ width: "18px", height: "18px", cursor: "pointer" }}
            />
            <label htmlFor={`${meta.fieldId}-thinking`} style={{ fontSize: "0.88rem", fontWeight: 700, color: "#0f172a", cursor: "pointer" }}>
              Enable Thinking / Reasoning output processing (for reasoning-enabled models)
            </label>
          </div>
        )}
      </div>

      {/* ===================================================================== */}
      {/* SECTION 4: ADVANCED OPTIONS & PROMPTS */}
      {/* ===================================================================== */}
      <div>
        <h3 css={sectionHeadingStyle}>4. Advanced Options & Configuration JSON</h3>
        <p css={sectionSubtitleStyle}>Direct parameter overrides, test generation directives, and prompt templates</p>

        {/* Configuration (JSON) Section */}
        <div style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <label htmlFor={`${meta.fieldId}-json-textarea`} css={fieldLabelStyle} style={{ margin: 0 }}>
              Configuration (JSON)
            </label>
            <button type="button" css={formatBtnStyle} onClick={handleFormatJson}>
              <FormatAlignIcon /> Format
            </button>
          </div>

          {/* Collapsible Examples Button */}
          <button
            type="button"
            css={examplesBtnStyle}
            onClick={() => setIsExamplesOpen(!isExamplesOpen)}
            aria-expanded={isExamplesOpen}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <CodeBracketsIcon /> Examples
            </span>
            <ChevronDownIcon
              style={{
                transform: isExamplesOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.18s ease",
              }}
            />
          </button>

          {/* Examples dropdown */}
          {isExamplesOpen && (
            <div css={exampleDropdownStyle}>
              <div style={{ fontWeight: 750, color: "#0f172a", marginBottom: "8px" }}>
                {meta.examplesTitle}
              </div>
              <ul style={{ margin: "0 0 12px 18px", padding: 0, lineHeight: 1.6 }}>
                {meta.exampleItems.map((ex, idx) => (
                  <li key={idx} style={{ marginBottom: "4px" }}>
                    <code style={{ background: "#e2e8f0", padding: "2px 6px", borderRadius: "4px", fontSize: "0.82rem" }}>
                      {ex.code}
                    </code>{" "}
                    {ex.desc}
                  </li>
                ))}
              </ul>
              <Button
                variant="secondary"
                onPress={handleLoadExample}
                style={{
                  fontSize: "0.8rem",
                  padding: "6px 14px",
                  height: "auto",
                }}
              >
                Apply Recommended Config
              </Button>
            </div>
          )}

          {/* JSON Editor Box */}
          <div css={jsonEditorWrapper}>
            <div css={jsonHeaderTab}>JSON</div>
            <textarea
              id={`${meta.fieldId}-json-textarea`}
              css={jsonTextArea}
              value={values.configJson}
              onChange={(e) => {
                onChange({ configJson: e.target.value });
                if (jsonError) setJsonError(null);
              }}
              placeholder="{}"
              rows={4}
            />
          </div>
          <div css={helpTextStyle}>{meta.configDescription}</div>
          {jsonError && (
            <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#ef4444", fontSize: "0.82rem", marginTop: "6px" }}>
              <AlertTriangleIcon /> {jsonError}
            </div>
          )}
        </div>

        {/* Accordion 1: Test Generation */}
        <div css={accordionCardStyle}>
          <button
            type="button"
            css={accordionHeaderStyle}
            onClick={() => setIsTestGenOpen(!isTestGenOpen)}
            aria-expanded={isTestGenOpen}
          >
            <div>
              <h4 css={accordionTitleStyle}>Test Generation</h4>
              <p css={accordionSubtitleStyle}>Configure how red team test cases are generated for this target</p>
            </div>
            <ChevronDownIcon
              style={{
                transform: isTestGenOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.18s ease",
              }}
            />
          </button>
          {isTestGenOpen && (
            <div css={accordionBodyStyle}>
              <label htmlFor={`${meta.fieldId}-test-gen`} css={fieldLabelStyle}>
                Instructions
              </label>
              <textarea
                id={`${meta.fieldId}-test-gen`}
                css={promptTextAreaStyle}
                style={{ width: "100%", minHeight: "80px" }}
                placeholder="e.g. Focus on prompt injection and guardrail evasion against system instructions..."
                value={values.testGenerationInstructions || ""}
                onChange={(e) => onChange({ testGenerationInstructions: e.target.value })}
              />
            </div>
          )}
        </div>

        {/* Accordion 2: Delay */}
        <div css={accordionCardStyle}>
          <button
            type="button"
            css={accordionHeaderStyle}
            onClick={() => setIsDelayOpen(!isDelayOpen)}
            aria-expanded={isDelayOpen}
          >
            <div>
              <h4 css={accordionTitleStyle}>Delay</h4>
              <p css={accordionSubtitleStyle}>Configure the delay between requests</p>
            </div>
            <ChevronDownIcon
              style={{
                transform: isDelayOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.18s ease",
              }}
            />
          </button>
          {isDelayOpen && (
            <div css={accordionBodyStyle}>
              <label htmlFor={`${meta.fieldId}-delay`} css={fieldLabelStyle}>
                Delay in Milliseconds
              </label>
              <input
                id={`${meta.fieldId}-delay`}
                type="number"
                min="0"
                step="50"
                css={textInputStyle(false)}
                style={{ width: "220px" }}
                value={values.delayMs ?? 0}
                onChange={(e) => onChange({ delayMs: Number(e.target.value) || 0 })}
              />
              <div css={helpTextStyle}>Throttle requests to prevent overwhelming local model server resources.</div>
            </div>
          )}
        </div>

        {/* Accordion 3: Extension Hook */}
        <div css={accordionCardStyle}>
          <button
            type="button"
            css={accordionHeaderStyle}
            onClick={() => setIsExtensionHookOpen(!isExtensionHookOpen)}
            aria-expanded={isExtensionHookOpen}
          >
            <div>
              <h4 css={accordionTitleStyle}>Extension Hook</h4>
              <p css={accordionSubtitleStyle}>Run custom code at specific points in the evaluation lifecycle</p>
            </div>
            <ChevronDownIcon
              style={{
                transform: isExtensionHookOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.18s ease",
              }}
            />
          </button>
          {isExtensionHookOpen && (
            <div css={accordionBodyStyle}>
              <label htmlFor={`${meta.fieldId}-hook`} css={fieldLabelStyle}>
                Hook File / Function
              </label>
              <input
                id={`${meta.fieldId}-hook`}
                type="text"
                css={textInputStyle(false)}
                placeholder="file://./custom_hook.py:before_call"
                value={values.extensionHook || ""}
                onChange={(e) => onChange({ extensionHook: e.target.value })}
              />
              <div css={helpTextStyle}>Optional path to hook function executed before or after target invocations.</div>
            </div>
          )}
        </div>

        {/* Prompts Section */}
        <div style={{ marginTop: "24px", marginBottom: "16px" }}>
          <h4 style={{ fontSize: "0.98rem", fontWeight: 800, margin: "0 0 4px 0", color: "#0f172a" }}>
            Prompts
          </h4>
          <p css={helpTextStyle} style={{ marginTop: 0, marginBottom: "14px" }}>
            Enter your prompts below. Use <code style={{ background: "#f1f5f9", padding: "2px 6px", borderRadius: "4px", fontWeight: 700 }}>{"{{ prompt }}"}</code> as a placeholder where probe input appears.
          </p>

          {values.prompts.map((promptText, idx) => (
            <div key={idx} css={promptCardStyle}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#475569", marginBottom: "4px" }}>
                  Prompt Template #{idx + 1}
                </div>
                <textarea
                  css={promptTextAreaStyle}
                  value={promptText}
                  onChange={(e) => handlePromptChange(idx, e.target.value)}
                  placeholder="{{prompt}}"
                />
              </div>
              {values.prompts.length > 1 && (
                <button
                  type="button"
                  css={deleteButtonStyle}
                  onClick={() => handleDeletePrompt(idx)}
                  title="Delete prompt template"
                  style={{ marginTop: "24px" }}
                >
                  <TrashIcon />
                </button>
              )}
            </div>
          ))}

          <button type="button" css={addPromptBtnStyle} onClick={handleAddPrompt}>
            <PlusIcon /> Add Prompt
          </button>
        </div>
      </div>

      {/* Bottom Navigation Buttons */}
      {!hideBottomBar && (
        <div css={bottomNavWrapper}>
          <button type="button" css={backBtnStyle} onClick={onBack}>
            &lt; Back
          </button>
          <button type="button" css={nextBtnStyle} onClick={handleNextClick}>
            Next &gt;
          </button>
        </div>
      )}
    </div>
  );
}
